import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertOctagon,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Loader2,
  Package,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Upload,
  Wrench,
  XCircle,
} from 'lucide-react';
import {
  EQUIPEMENT_CRITICITES,
  EQUIPEMENT_STATUTS,
  EQUIPEMENT_STATUTS_AFFECTATION,
  EQUIPEMENT_TYPES,
  type EquipementCriticite,
  type EquipementStatut,
  type EquipementStatutAffectation,
  type EquipementType,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { EquipementFormModal } from '../components/equipements/EquipementFormModal';
import { EquipementDetailDrawer } from '../components/equipements/EquipementDetailDrawer';
import { ImportEquipementsModal } from '../components/equipements/ImportEquipementsModal';
import { ScanQrModal } from '../components/equipements/ScanQrModal';
import { SavedFilterMenu } from '../components/equipements/SavedFilterMenu';
import { StatutEquipementBadge, CriticiteBadge } from '../components/equipements/badges';

/**
 * Page `/equipements` (US-EQ-01 / 02 / 03 / 11 / 12).
 *
 *  - KPIs (total / en service / en panne / en maintenance / vitaux sans
 *    maintenance préventive).
 *  - Toolbar : filtres sauvegardés (US-EQ-12) + filtres rapides + recherche
 *    + bouton "Plus de filtres" (panel collapse) avec filtres avancés
 *    (service / bâtiment / étage / salle / responsable / utilisateur affecté /
 *    statut_affectation / mobile).
 *  - Table paginée + tri par colonne.
 *  - Click ligne → drawer 360°.
 *  - Bouton "Scan QR" → ouvre `<ScanQrModal>` qui appelle `/equipements/scan`.
 */

interface EquipementRow {
  id: string;
  code_equipement: string;
  designation: string;
  type: EquipementType;
  statut: EquipementStatut;
  criticite: EquipementCriticite | null;
  service_id: string | null;
  batiment_id: string | null;
  etage_id: string | null;
  salle_id: string | null;
  utilisateur_affecte_id: string | null;
  statut_affectation: EquipementStatutAffectation;
  est_mobile: boolean;
  service: { id: string; nom_complet: string } | null;
  salle: { id: string; nom: string } | null;
  utilisateur_affecte: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface Stats {
  total: number;
  en_service: number;
  en_panne: number;
  en_maintenance: number;
  reforme: number;
  en_stock: number;
  critiques_sans_maintenance: number;
  etalonnages_echus: number;
}

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}
interface BatimentOption {
  id: string;
  code: string;
  nom: string;
}
interface EtageOption {
  id: string;
  numero: string;
  nom: string | null;
}
interface SalleOption {
  id: string;
  code: string;
  nom: string;
}
interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
}

type SortBy = 'code_equipement' | 'designation' | 'type' | 'statut' | 'criticite' | 'created_at';
type SortOrder = 'asc' | 'desc';

interface FiltersState {
  search: string;
  type: string;
  statut: string;
  criticite: string;
  service_id: string;
  batiment_id: string;
  etage_id: string;
  salle_id: string;
  responsable_id: string;
  utilisateur_affecte_id: string;
  statut_affectation: string;
  est_mobile: '' | 'true' | 'false';
}

const EMPTY_FILTERS: FiltersState = {
  search: '',
  type: '',
  statut: '',
  criticite: '',
  service_id: '',
  batiment_id: '',
  etage_id: '',
  salle_id: '',
  responsable_id: '',
  utilisateur_affecte_id: '',
  statut_affectation: '',
  est_mobile: '',
};

export function EquipementsPage() {
  const { can } = usePermissions();
  const canRead = can('equipements', 'read');
  const canCreate = can('equipements', 'update');

  const [items, setItems] = useState<EquipementRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [sortBy, setSortBy] = useState<SortBy>('designation');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    setErrorMessage(null);
    try {
      const res = await api.get('/equipements/export.xlsx', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `equipements-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [batiments, setBatiments] = useState<BatimentOption[]>([]);
  const [etages, setEtages] = useState<EtageOption[]>([]);
  const [salles, setSalles] = useState<SalleOption[]>([]);
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filters.type,
    filters.statut,
    filters.criticite,
    filters.service_id,
    filters.batiment_id,
    filters.etage_id,
    filters.salle_id,
    filters.responsable_id,
    filters.utilisateur_affecte_id,
    filters.statut_affectation,
    filters.est_mobile,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (!canRead) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    filters.type,
    filters.statut,
    filters.criticite,
    filters.service_id,
    filters.batiment_id,
    filters.etage_id,
    filters.salle_id,
    filters.responsable_id,
    filters.utilisateur_affecte_id,
    filters.statut_affectation,
    filters.est_mobile,
    sortBy,
    sortOrder,
    page,
  ]);

  useEffect(() => {
    if (canRead) loadStats();
  }, [canRead]);

  // Charge les options secondaires une fois.
  useEffect(() => {
    if (!canRead) return;
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
    api
      .get<{ data: BatimentOption[] }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => setBatiments([]));
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
  }, [canRead]);

  // Cascade étages.
  useEffect(() => {
    if (!filters.batiment_id) {
      setEtages([]);
      return;
    }
    api
      .get<{ data: EtageOption[] }>(`/etages?batiment_id=${filters.batiment_id}`)
      .then((res) => setEtages(res.data.data))
      .catch(() => setEtages([]));
  }, [filters.batiment_id]);

  // Cascade salles.
  useEffect(() => {
    if (!filters.etage_id) {
      setSalles([]);
      return;
    }
    api
      .get<{ data: SalleOption[] }>(`/salles?etage_id=${filters.etage_id}&page_size=200`)
      .then((res) => setSalles(res.data.data))
      .catch(() => setSalles([]));
  }, [filters.etage_id]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filters.type) params.set('type', filters.type);
      if (filters.statut) params.set('statut', filters.statut);
      if (filters.criticite) params.set('criticite', filters.criticite);
      if (filters.service_id) params.set('service_id', filters.service_id);
      if (filters.batiment_id) params.set('batiment_id', filters.batiment_id);
      if (filters.etage_id) params.set('etage_id', filters.etage_id);
      if (filters.salle_id) params.set('salle_id', filters.salle_id);
      if (filters.responsable_id) params.set('responsable_id', filters.responsable_id);
      if (filters.utilisateur_affecte_id)
        params.set('utilisateur_affecte_id', filters.utilisateur_affecte_id);
      if (filters.statut_affectation) params.set('statut_affectation', filters.statut_affectation);
      if (filters.est_mobile) params.set('est_mobile', filters.est_mobile);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('page', String(page));
      params.set('page_size', '50');
      const res = await api.get<{ data: EquipementRow[]; pagination: Pagination }>(
        `/equipements?${params.toString()}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await api.get<{ data: Stats }>('/equipements/stats');
      setStats(res.data.data);
    } catch {
      // Silencieux.
    }
  }

  function toggleSort(col: SortBy) {
    if (sortBy === col) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortOrder('asc');
    }
  }

  function refreshAll() {
    refresh();
    loadStats();
  }

  function updateFilter<K extends keyof FiltersState>(key: K, value: FiltersState[K]) {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'batiment_id') {
        next.etage_id = '';
        next.salle_id = '';
      } else if (key === 'etage_id') {
        next.salle_id = '';
      }
      return next;
    });
  }

  function applySavedFilter(payload: Record<string, unknown>) {
    setFilters({
      ...EMPTY_FILTERS,
      ...(payload as Partial<FiltersState>),
    });
  }

  const currentFilters = useMemo(() => {
    const out: Record<string, unknown> = {};
    (Object.keys(filters) as Array<keyof FiltersState>).forEach((k) => {
      const v = filters[k];
      if (v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  }, [filters]);

  const activeFilterCount = Object.keys(currentFilters).filter((k) => k !== 'search').length;

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Équipements.</p>
      </div>
    );
  }

  const totalLabel = pagination
    ? `${pagination.total} équipement(s) au total`
    : `${items.length} équipement(s) chargé(s)`;

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Wrench className="w-7 h-7 mr-2 text-primary-600" />
            Équipements
          </h1>
          <p className="text-sm text-gray-500 mt-1">{totalLabel}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={refreshAll}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setScanOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
          >
            <QrCode className="w-4 h-4 mr-1.5" />
            Scan QR
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            title="Exporter en Excel"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Exporter
          </button>
          {canCreate && (
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
              title="Importer depuis Excel"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              Importer
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvel équipement
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Total" value={stats.total} icon={Wrench} tone="primary" />
          <KpiCard label="En service" value={stats.en_service} icon={Package} tone="green" />
          <KpiCard label="En panne" value={stats.en_panne} icon={XCircle} tone="red" />
          <KpiCard
            label="En maintenance"
            value={stats.en_maintenance}
            icon={Wrench}
            tone="orange"
          />
          <KpiCard
            label="Critiques sans maintenance"
            value={stats.critiques_sans_maintenance}
            icon={AlertOctagon}
            tone="purple"
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <SavedFilterMenu currentFilters={currentFilters} onApply={applySavedFilter} />
            <select
              value={filters.type}
              onChange={(e) => updateFilter('type', e.target.value)}
              className={selectClass}
            >
              <option value="">Tous les types</option>
              {EQUIPEMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={filters.statut}
              onChange={(e) => updateFilter('statut', e.target.value)}
              className={selectClass}
            >
              <option value="">Tous les statuts</option>
              {EQUIPEMENT_STATUTS.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <select
              value={filters.criticite}
              onChange={(e) => updateFilter('criticite', e.target.value)}
              className={selectClass}
            >
              <option value="">Toutes les criticités</option>
              {EQUIPEMENT_CRITICITES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher…"
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className={`inline-flex items-center px-3 py-1.5 border rounded-lg text-sm font-medium ${
                showAdvanced || activeFilterCount > 0
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
              Plus de filtres
              {activeFilterCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AdvField label="Service">
                <select
                  value={filters.service_id}
                  onChange={(e) => updateFilter('service_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.nom_complet}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Bâtiment">
                <select
                  value={filters.batiment_id}
                  onChange={(e) => updateFilter('batiment_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {batiments.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {b.nom}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Étage">
                <select
                  value={filters.etage_id}
                  onChange={(e) => updateFilter('etage_id', e.target.value)}
                  className={selectClass + ' w-full'}
                  disabled={!filters.batiment_id}
                >
                  <option value="">— Tous —</option>
                  {etages.map((et) => (
                    <option key={et.id} value={et.id}>
                      Étage {et.numero}
                      {et.nom ? ` — ${et.nom}` : ''}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Salle">
                <select
                  value={filters.salle_id}
                  onChange={(e) => updateFilter('salle_id', e.target.value)}
                  className={selectClass + ' w-full'}
                  disabled={!filters.etage_id}
                >
                  <option value="">— Toutes —</option>
                  {salles.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.nom}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Responsable">
                <select
                  value={filters.responsable_id}
                  onChange={(e) => updateFilter('responsable_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Utilisateur affecté">
                <select
                  value={filters.utilisateur_affecte_id}
                  onChange={(e) => updateFilter('utilisateur_affecte_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Statut d'affectation">
                <select
                  value={filters.statut_affectation}
                  onChange={(e) => updateFilter('statut_affectation', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {EQUIPEMENT_STATUTS_AFFECTATION.map((s) => (
                    <option key={s} value={s}>
                      {s.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Mobilité">
                <select
                  value={filters.est_mobile}
                  onChange={(e) =>
                    updateFilter('est_mobile', e.target.value as FiltersState['est_mobile'])
                  }
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Indifférent —</option>
                  <option value="true">Mobile</option>
                  <option value="false">Fixe</option>
                </select>
              </AdvField>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900"
              >
                <Filter className="w-3.5 h-3.5 mr-1" />
                Réinitialiser
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader
                  label="Code"
                  col="code_equipement"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <SortHeader
                  label="Désignation"
                  col="designation"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <SortHeader
                  label="Type"
                  col="type"
                  className="hidden md:table-cell"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <SortHeader label="Statut" col="statut" {...{ sortBy, sortOrder, toggleSort }} />
                <SortHeader
                  label="Criticité"
                  col="criticite"
                  className="hidden lg:table-cell"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Localisation
                </th>
                <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Affecté à
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucun équipement. Créez le premier pour démarrer.
                  </td>
                </tr>
              ) : (
                items.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setDetailId(e.id)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5 text-xs font-semibold text-primary-600 uppercase tracking-wide whitespace-nowrap">
                      {e.code_equipement}
                    </td>
                    <td className="px-3 py-2.5 text-gray-900">
                      {e.designation}
                      {e.est_mobile && (
                        <span className="ml-1.5 inline-block text-[9px] font-medium text-gray-500 border border-gray-200 rounded px-1">
                          mobile
                        </span>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-gray-600">{e.type}</td>
                    <td className="px-3 py-2.5">
                      <StatutEquipementBadge statut={e.statut} />
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5">
                      {e.criticite ? <CriticiteBadge criticite={e.criticite} /> : '—'}
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 text-gray-600 truncate max-w-[180px]">
                      {e.service?.nom_complet ?? '—'}
                    </td>
                    <td className="hidden xl:table-cell px-3 py-2.5 text-gray-600 truncate max-w-[160px]">
                      {e.salle?.nom ?? '—'}
                    </td>
                    <td className="hidden xl:table-cell px-3 py-2.5 text-gray-600 truncate max-w-[160px]">
                      {e.utilisateur_affecte ? (
                        `${e.utilisateur_affecte.first_name} ${e.utilisateur_affecte.last_name}`
                      ) : e.statut_affectation === 'en_attente_acceptation' ? (
                        <span className="text-yellow-700">en attente</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-400">
                      <ChevronRight className="w-4 h-4 inline" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              Page {pagination.page} sur {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={pagination.page === pagination.total_pages}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <EquipementFormModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refreshAll();
          }}
        />
      )}

      {detailId && (
        <EquipementDetailDrawer
          equipementId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refreshAll}
        />
      )}

      {scanOpen && (
        <ScanQrModal
          onClose={() => setScanOpen(false)}
          onFound={(eq) => {
            setScanOpen(false);
            setDetailId(eq.id);
          }}
        />
      )}

      {importOpen && (
        <ImportEquipementsModal
          onClose={() => setImportOpen(false)}
          onCompleted={() => {
            setImportOpen(false);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const selectClass =
  'px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

function SortHeader({
  label,
  col,
  sortBy,
  sortOrder,
  toggleSort,
  className,
}: {
  label: string;
  col: SortBy;
  sortBy: SortBy;
  sortOrder: SortOrder;
  toggleSort(col: SortBy): void;
  className?: string;
}) {
  const active = sortBy === col;
  return (
    <th
      onClick={() => toggleSort(col)}
      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none ${className ?? ''}`}
    >
      <span className="inline-flex items-center">
        {label}
        {active ? (
          sortOrder === 'asc' ? (
            <ArrowUp className="w-3 h-3 ml-1" />
          ) : (
            <ArrowDown className="w-3 h-3 ml-1" />
          )
        ) : null}
      </span>
    </th>
  );
}

function AdvField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: typeof Wrench;
  tone: 'default' | 'primary' | 'green' | 'red' | 'orange' | 'purple';
}) {
  const tones: Record<typeof tone, string> = {
    default: 'text-gray-700',
    primary: 'text-primary-600',
    green: 'text-green-600',
    red: 'text-red-600',
    orange: 'text-orange-600',
    purple: 'text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
}
